/**
 * WorkspaceAnalytics — Event tracking for the Workspace Shell
 * 
 * Tracks key user actions to measure activation and engagement
 * in the Search → Screen → Learn workflow.
 * 
 * Events are sent to the backend for aggregation and analysis.
 */
import apiClient from '../api';

// Event types for the workspace workflow
export type WorkspaceEvent =
  // Navigation events
  | 'workspace_opened'
  | 'search_opened'
  | 'screen_opened'
  | 'learn_opened'
  // Search workflow
  | 'search_run'
  | 'article_saved_from_search'
  // Screening workflow
  | 'screen_article_saved'
  | 'screen_article_deferred'
  | 'screen_article_skipped'
  | 'screening_session_completed'
  // Learn workflow
  | 'article_opened_in_learn'
  | 'litscholar_opened_from_learn'
  | 'audio_generated_from_learn'
  | 'audio_played_from_learn'
  // Discussion workflow
  | 'discussion_opened_from_article'
  | 'discussion_post_started_from_article'
  // Onboarding
  | 'digest_setup_started'
  | 'digest_setup_completed'
  | 'first_digest_run';

export interface AnalyticsEventData {
  event: WorkspaceEvent;
  properties?: Record<string, any>;
  timestamp?: string;
}

// In-memory queue for batching events
let eventQueue: AnalyticsEventData[] = [];
let flushTimeout: NodeJS.Timeout | null = null;
const FLUSH_INTERVAL = 5000; // 5 seconds
const MAX_QUEUE_SIZE = 20;

/**
 * Track a workspace event
 * Events are queued and sent in batches to reduce API calls
 */
export const trackEvent = (event: WorkspaceEvent, properties?: Record<string, any>) => {
  const eventData: AnalyticsEventData = {
    event,
    properties: {
      ...properties,
      // Add common properties
      url: window.location.pathname,
      referrer: document.referrer || null,
    },
    timestamp: new Date().toISOString(),
  };

  // Add to queue
  eventQueue.push(eventData);

  // Log in development
  if (process.env.NODE_ENV === 'development') {
    console.log('[Analytics]', event, properties);
  }

  // Flush if queue is full
  if (eventQueue.length >= MAX_QUEUE_SIZE) {
    flushEvents();
  } else if (!flushTimeout) {
    // Schedule flush
    flushTimeout = setTimeout(flushEvents, FLUSH_INTERVAL);
  }
};

/**
 * Flush queued events to the backend
 */
const flushEvents = async () => {
  if (flushTimeout) {
    clearTimeout(flushTimeout);
    flushTimeout = null;
  }

  if (eventQueue.length === 0) return;

  const eventsToSend = [...eventQueue];
  eventQueue = [];

  try {
    await apiClient.post('/workspace/analytics/events', { events: eventsToSend });
  } catch (error) {
    // On failure, re-queue events (up to max size)
    console.error('[Analytics] Failed to send events:', error);
    eventQueue = [...eventsToSend.slice(-MAX_QUEUE_SIZE), ...eventQueue].slice(0, MAX_QUEUE_SIZE);
  }
};

/**
 * Flush events immediately (e.g., on page unload)
 */
export const flushImmediately = () => {
  flushEvents();
};

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushImmediately);
  window.addEventListener('pagehide', flushImmediately);
}

// Helper functions for common tracking patterns

/**
 * Track page view for workspace pages
 */
export const trackPageView = (page: 'search' | 'screen' | 'learn' | 'workspace') => {
  const eventMap: Record<string, WorkspaceEvent> = {
    workspace: 'workspace_opened',
    search: 'search_opened',
    screen: 'screen_opened',
    learn: 'learn_opened',
  };
  trackEvent(eventMap[page]);
};

/**
 * Track search execution
 */
export const trackSearch = (query: string, filters?: Record<string, any>, resultCount?: number) => {
  trackEvent('search_run', {
    query_length: query.length,
    has_filters: !!filters && Object.keys(filters).length > 0,
    result_count: resultCount,
  });
};

/**
 * Track article save from search
 */
export const trackSaveFromSearch = (pmid: string, folder?: string) => {
  trackEvent('article_saved_from_search', {
    pmid,
    folder: folder || 'inbox',
  });
};

/**
 * Track screening action
 */
export const trackScreeningAction = (
  action: 'saved' | 'deferred' | 'skipped',
  pmid: string,
  digestId?: string
) => {
  const eventMap: Record<string, WorkspaceEvent> = {
    saved: 'screen_article_saved',
    deferred: 'screen_article_deferred',
    skipped: 'screen_article_skipped',
  };
  trackEvent(eventMap[action], { pmid, digest_id: digestId });
};

/**
 * Track screening session completion
 */
export const trackScreeningComplete = (
  digestId: string,
  stats: { saved: number; deferred: number; skipped: number; total: number }
) => {
  trackEvent('screening_session_completed', {
    digest_id: digestId,
    ...stats,
    completion_rate: stats.total > 0 ? ((stats.saved + stats.deferred + stats.skipped) / stats.total) : 0,
  });
};

/**
 * Track article opened in Learn
 */
export const trackArticleOpened = (pmid: string, folder?: string) => {
  trackEvent('article_opened_in_learn', { pmid, folder });
};

/**
 * Track LitScholar opened from Learn
 */
export const trackLitScholarOpened = (pmid: string, source: 'drawer' | 'full_page' | 'card_button') => {
  trackEvent('litscholar_opened_from_learn', { pmid, source });
};

/**
 * Track audio generation from Learn
 */
export const trackAudioGenerated = (pmid: string) => {
  trackEvent('audio_generated_from_learn', { pmid });
};

/**
 * Track audio playback from Learn
 */
export const trackAudioPlayed = (pmid: string, duration?: number) => {
  trackEvent('audio_played_from_learn', { pmid, duration });
};

/**
 * Track discussion interaction
 */
export const trackDiscussion = (action: 'opened' | 'post_started', pmid?: string, threadId?: string) => {
  const event: WorkspaceEvent = action === 'opened' 
    ? 'discussion_opened_from_article' 
    : 'discussion_post_started_from_article';
  trackEvent(event, { pmid, thread_id: threadId });
};

/**
 * Track onboarding events
 */
export const trackOnboarding = (action: 'started' | 'completed' | 'first_digest') => {
  const eventMap: Record<string, WorkspaceEvent> = {
    started: 'digest_setup_started',
    completed: 'digest_setup_completed',
    first_digest: 'first_digest_run',
  };
  trackEvent(eventMap[action]);
};

export default {
  trackEvent,
  trackPageView,
  trackSearch,
  trackSaveFromSearch,
  trackScreeningAction,
  trackScreeningComplete,
  trackArticleOpened,
  trackLitScholarOpened,
  trackAudioGenerated,
  trackAudioPlayed,
  trackDiscussion,
  trackOnboarding,
  flushImmediately,
};
